import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { SimpleStrategyAiUse, SimpleStrategyInitiative, SimpleStrategyPriority } from './p4-api'

function DialogShell({ title, description, onClose, children }: { title: string; description?: string; onClose: () => void; children: ReactNode }) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    const prior = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab' || !dialogRef.current) return
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      if (!focusable.length) return
      const first = focusable[0]!
      const last = focusable[focusable.length - 1]!
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    window.requestAnimationFrame(() => dialogRef.current?.querySelector<HTMLElement>('input, textarea, select, button')?.focus())
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      prior?.focus()
    }
  }, [])

  return <div className="simple-strategy-dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
    <div className="simple-strategy-dialog" role="dialog" aria-modal="true" aria-labelledby="simple-strategy-dialog-title" ref={dialogRef}>
      <header><div><h2 id="simple-strategy-dialog-title">{title}</h2>{description ? <p>{description}</p> : null}</div><button className="simple-strategy-dialog-close" type="button" aria-label={`Close ${title}`} onClick={onClose}>×</button></header>
      {children}
    </div>
  </div>
}

export function StrategyDetailsDialog({ initial, pending, onClose, onSave }: {
  initial?: { name: string; startYear: number | null; endYear: number | null }
  pending: boolean
  onClose: () => void
  onSave: (value: { name: string; startYear: number; endYear: number }) => void
}) {
  const currentYear = new Date().getFullYear()
  const [name, setName] = useState(initial?.name ?? '')
  const [startYear, setStartYear] = useState(String(initial?.startYear ?? currentYear))
  const [endYear, setEndYear] = useState(String(initial?.endYear ?? currentYear + 3))
  const valid = name.trim().length > 0 && Number(startYear) >= 1900 && Number(endYear) <= 2200 && Number(endYear) >= Number(startYear)
  return <DialogShell title={initial ? 'Edit strategy' : 'Create strategy'} description="Set the strategy name and the years shown on the roadmap." onClose={onClose}>
    <div className="simple-strategy-form">
      <label><span>Strategy name</span><input value={name} onChange={(event) => setName(event.target.value)} /></label>
      <div className="simple-strategy-year-fields">
        <label><span>Start year</span><input type="number" min="1900" max="2200" value={startYear} onChange={(event) => setStartYear(event.target.value)} /></label>
        <label><span>End year</span><input type="number" min={startYear || '1900'} max="2200" value={endYear} onChange={(event) => setEndYear(event.target.value)} /></label>
      </div>
      <div className="simple-strategy-dialog-actions"><button className="button secondary" type="button" onClick={onClose}>Cancel</button><button className="button primary" type="button" disabled={!valid || pending} onClick={() => onSave({ name: name.trim(), startYear: Number(startYear), endYear: Number(endYear) })}>{pending ? 'Saving…' : 'Save strategy'}</button></div>
    </div>
  </DialogShell>
}

export function PriorityDialog({ initial, pending, onClose, onSave }: {
  initial?: SimpleStrategyPriority
  pending: boolean
  onClose: () => void
  onSave: (value: { title: string; description: string }) => void
}) {
  const [title, setTitle] = useState(initial?.title ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const valid = title.trim().length > 0
  return <DialogShell title={initial ? 'Edit strategic priority' : 'Add strategic priority'} onClose={onClose}>
    <div className="simple-strategy-form">
      <label><span>Priority name <strong aria-hidden="true">*</strong></span><input value={title} onChange={(event) => setTitle(event.target.value)} required /></label>
      <label><span>Description (optional)</span><textarea rows={3} value={description} onChange={(event) => setDescription(event.target.value)} /></label>
      <div className="simple-strategy-dialog-actions"><button className="button secondary" type="button" onClick={onClose}>Cancel</button><button className="button primary" type="button" disabled={!valid || pending} onClick={() => onSave({ title: title.trim(), description: description.trim() })}>{pending ? 'Saving…' : initial ? 'Save priority' : 'Add priority'}</button></div>
    </div>
  </DialogShell>
}

const quarters = [1, 2, 3, 4]

export function AddInitiativeChoiceDialog({ priorityTitle, onClose, onCreate, onLink }: {
  priorityTitle: string
  onClose: () => void
  onCreate: () => void
  onLink: () => void
}) {
  return <DialogShell title="Add an initiative" description="Create a new AI initiative for this strategic priority, or link an existing initiative." onClose={onClose}>
    <div className="simple-initiative-choice-list">
      <p className="simple-dialog-context"><strong>{priorityTitle}</strong></p>
      <button type="button" className="simple-initiative-choice" onClick={onCreate}><strong>Create new initiative</strong><span>Create a new AI Initiative and automatically link it to this Strategic Priority.</span></button>
      <button type="button" className="simple-initiative-choice" onClick={onLink}><strong>Link existing initiative</strong><span>Select an existing AI Initiative and link it to this Strategic Priority.</span></button>
    </div>
  </DialogShell>
}

export function InitiativeDialog({ initial, strategyStartYear, strategyEndYear, endHelperText, pending, onClose, onSave }: {
  initial?: SimpleStrategyInitiative
  strategyStartYear: number
  strategyEndYear: number
  endHelperText?: string
  pending: boolean
  onClose: () => void
  onSave: (value: { name: string; description: string; startYear: number; startQuarter: number; endYear: number | null; endQuarter: number | null }) => void
}) {
  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [startYear, setStartYear] = useState(String(initial?.startYear ?? strategyStartYear))
  const [startQuarter, setStartQuarter] = useState(String(initial?.startQuarter ?? 1))
  const [endYear, setEndYear] = useState(initial?.endYear === null || initial?.endYear === undefined ? '' : String(initial.endYear))
  const [endQuarter, setEndQuarter] = useState(initial?.endQuarter === null || initial?.endQuarter === undefined ? '' : String(initial.endQuarter))
  const startIndex = Number(startYear) * 4 + Number(startQuarter)
  const hasEnd = Boolean(endYear || endQuarter)
  const endComplete = Boolean(endYear && endQuarter)
  const endIndex = endComplete ? Number(endYear) * 4 + Number(endQuarter) : null
  const valid = name.trim().length > 0 && Number(startYear) >= strategyStartYear && Number(startYear) <= strategyEndYear && (!hasEnd || (endComplete && Number(endYear) <= strategyEndYear && endIndex! >= startIndex))
  const years = Array.from({ length: strategyEndYear - strategyStartYear + 1 }, (_, index) => strategyStartYear + index)

  return <DialogShell title={initial ? 'Edit AI initiative' : 'Add AI initiative'} onClose={onClose}>
    <div className="simple-strategy-form">
      <label><span>Initiative name <strong aria-hidden="true">*</strong></span><input value={name} onChange={(event) => setName(event.target.value)} required /></label>
      <label><span>Description (optional)</span><textarea rows={3} value={description} onChange={(event) => setDescription(event.target.value)} /></label>
      <div className="simple-initiative-timing-fields">
        <label><span>Planned from — year <strong aria-hidden="true">*</strong></span><select value={startYear} onChange={(event) => setStartYear(event.target.value)}>{years.map((year) => <option key={year} value={year}>{year}</option>)}</select></label>
        <label><span>Planned from — quarter <strong aria-hidden="true">*</strong></span><select value={startQuarter} onChange={(event) => setStartQuarter(event.target.value)}>{quarters.map((quarter) => <option key={quarter} value={quarter}>Q{quarter}</option>)}</select></label>
        <label><span>Planned until (optional) — year</span><select value={endYear} onChange={(event) => { const value = event.target.value; setEndYear(value); if (!value) setEndQuarter('') }}><option value="">Ongoing</option>{years.map((year) => <option key={year} value={year}>{year}</option>)}</select></label>
        <label><span>Planned until (optional) — quarter</span><select value={endQuarter} disabled={!endYear} onChange={(event) => setEndQuarter(event.target.value)}><option value="">Choose quarter</option>{quarters.map((quarter) => <option key={quarter} value={quarter}>Q{quarter}</option>)}</select></label>
      </div>
      <p className="simple-field-help">{endHelperText ?? 'Leave blank if the initiative is ongoing. Only enter an end period if the initiative is expected to formally finish or stop.'}</p>
      {endComplete && endIndex! < startIndex ? <p className="form-error" role="alert">The end quarter must be the same as or later than the start quarter.</p> : null}
      {hasEnd && !endComplete ? <p className="form-error" role="alert">Choose both an end year and end quarter, or leave both blank.</p> : null}
      <div className="simple-strategy-dialog-actions"><button className="button secondary" type="button" onClick={onClose}>Cancel</button><button className="button primary" type="button" disabled={!valid || pending} onClick={() => onSave({ name: name.trim(), description: description.trim(), startYear: Number(startYear), startQuarter: Number(startQuarter), endYear: endComplete ? Number(endYear) : null, endQuarter: endComplete ? Number(endQuarter) : null })}>{pending ? 'Saving…' : initial ? 'Save initiative' : 'Add initiative'}</button></div>
    </div>
  </DialogShell>
}

export function InitiativeSelectionDialog({ priorityTitle, initiatives, selectedIds, pending, onClose, onSave }: {
  priorityTitle: string
  initiatives: SimpleStrategyInitiative[]
  selectedIds: string[]
  pending: boolean
  onClose: () => void
  onSave: (ids: string[]) => void
}) {
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const visible = useMemo(() => initiatives.filter((initiative) => initiative.name.toLowerCase().includes(search.trim().toLowerCase())), [initiatives, search])
  const toggle = (id: string) => setSelected((current) => current.includes(id) ? current.filter((value) => value !== id) : [...current, id])
  const timing = (initiative: SimpleStrategyInitiative) => {
    if (initiative.startYear === null || initiative.startQuarter === null) return 'Timing not set'
    if (initiative.endYear === null || initiative.endQuarter === null) return `From Q${initiative.startQuarter} ${initiative.startYear} · Ongoing`
    return `Q${initiative.startQuarter} ${initiative.startYear} – Q${initiative.endQuarter} ${initiative.endYear}`
  }
  return <DialogShell title="Link AI initiatives" description="Select the AI initiatives that support this strategic priority." onClose={onClose}>
    <div className="simple-strategy-form">
      <p className="simple-dialog-context"><strong>{priorityTitle}</strong></p>
      <label><span>Search AI initiatives</span><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by name" /></label>
      <fieldset className="simple-strategy-ai-use-picker"><legend>AI initiatives</legend>
        {visible.length ? visible.map((initiative) => {
          const alreadyLinked = selectedIds.includes(initiative.id)
          return <label key={initiative.id} className={alreadyLinked || selected.includes(initiative.id) ? 'selected' : undefined}><input type="checkbox" checked={alreadyLinked || selected.includes(initiative.id)} disabled={alreadyLinked} onChange={() => toggle(initiative.id)} /><span><strong>{initiative.name}</strong><small>{timing(initiative)} · {initiative.aiUses.length} linked AI {initiative.aiUses.length === 1 ? 'use' : 'uses'}{initiative.priorities.length ? ` · Supports ${initiative.priorities.map((priority) => priority.title).join(', ')}` : ''}{alreadyLinked ? ' · Already linked' : ''}</small></span></label>
        }) : <p>No AI initiatives match your search.</p>}
      </fieldset>
      <div className="simple-strategy-dialog-actions"><button className="button secondary" type="button" onClick={onClose}>Cancel</button><button className="button primary" type="button" disabled={pending || selected.length === 0} onClick={() => onSave([...selectedIds, ...selected])}>{pending ? 'Linking…' : 'Link selected initiatives'}</button></div>
    </div>
  </DialogShell>
}

export function PrioritySelectionDialog({ initiativeName, priorities, selectedIds, pending, onClose, onSave }: {
  initiativeName: string
  priorities: SimpleStrategyPriority[]
  selectedIds: string[]
  pending: boolean
  onClose: () => void
  onSave: (ids: string[]) => void
}) {
  const [selected, setSelected] = useState(selectedIds)
  const toggle = (id: string) => setSelected((current) => current.includes(id) ? current.filter((value) => value !== id) : [...current, id])
  return <DialogShell title="Link strategic priorities" description={`Select the strategic priorities supported by ${initiativeName}.`} onClose={onClose}>
    <div className="simple-strategy-form">
      <fieldset className="simple-strategy-ai-use-picker"><legend>Strategic priorities</legend>
        {priorities.length ? priorities.map((priority) => <label key={priority.id} className={selected.includes(priority.id) ? 'selected' : undefined}><input type="checkbox" checked={selected.includes(priority.id)} onChange={() => toggle(priority.id)} /><span><strong>{priority.title}</strong>{priority.description ? <small>{priority.description}</small> : null}</span></label>) : <p>No strategic priorities are available.</p>}
      </fieldset>
      <div className="simple-strategy-dialog-actions"><button className="button secondary" type="button" onClick={onClose}>Cancel</button><button className="button primary" type="button" disabled={pending} onClick={() => onSave(selected)}>{pending ? 'Saving…' : 'Save alignment'}</button></div>
    </div>
  </DialogShell>
}

const lifecycleLabels: Record<string, string> = {
  proposed: 'Proposed', experiment: 'Experiment', pilot: 'Pilot', production: 'Production', suspended: 'Suspended', retired: 'Retired',
}

export function AiUseSelectionDialog({ initiativeName, aiUses, selectedIds, pending, onClose, onSave }: {
  initiativeName: string
  aiUses: SimpleStrategyAiUse[]
  selectedIds: string[]
  pending: boolean
  onClose: () => void
  onSave: (ids: string[]) => void
}) {
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState(selectedIds)
  const visible = useMemo(() => aiUses.filter((use) => use.title.toLowerCase().includes(search.trim().toLowerCase())), [aiUses, search])
  const toggle = (id: string) => setSelected((current) => current.includes(id) ? current.filter((value) => value !== id) : [...current, id])
  return <DialogShell title="Link AI uses" description={`Select the AI uses that belong to ${initiativeName}.`} onClose={onClose}>
    <div className="simple-strategy-form">
      <label><span>Search AI uses</span><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by name" /></label>
      <fieldset className="simple-strategy-ai-use-picker"><legend>AI uses</legend>
        {visible.length ? visible.map((use) => <label key={use.id} className={selected.includes(use.id) ? 'selected' : undefined}><input type="checkbox" checked={selected.includes(use.id)} onChange={() => toggle(use.id)} /><span><strong>{use.title}</strong><small>{lifecycleLabels[use.operationalState] ?? use.operationalState}</small></span></label>) : <p>No AI uses match your search.</p>}
      </fieldset>
      <div className="simple-strategy-dialog-actions"><button className="button secondary" type="button" onClick={onClose}>Cancel</button><button className="button primary" type="button" disabled={pending} onClick={() => onSave(selected)}>{pending ? 'Linking…' : 'Link selected AI uses'}</button></div>
    </div>
  </DialogShell>
}
